import { cn, Icon, IconName, IconSize, IconVariant, Tooltip } from "@quortex/front-common"
import Highcharts from "highcharts"
import highchartsAccessibility from "highcharts/modules/accessibility"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import type { ChartProps } from "@/components/Chart"
import emptyEventList from "@/images/illustration-empty-event-list.svg?url"

highchartsAccessibility(Highcharts)

const ResolvedHighchartsReact =
  (HighchartsReact as unknown as { default?: typeof HighchartsReact }).default ?? HighchartsReact

const Chart = ({ series, options, className }: ChartProps) => {
  const hasLegend = series.some(serie => !!serie.name)

  const optionsWithSeries = useMemo(
    () => ({
      ...options,
      series
    }),
    [options, series]
  )

  const seriesFiltered = useMemo(
    () => series.filter(s => s.data.some(point => point?.[1] && point[1] > 0)),
    [series]
  )

  return seriesFiltered && seriesFiltered.length > 0 ? (
    <div
      className={cn(
        "grid gap-sm desktop:overflow-hidden",
        hasLegend && "desktop:grid-cols-5",
        className
      )}
    >
      <div
        className={cn(
          "desktop:col-span-4 max-w-[530px] tablet:max-w-full overflow-x-auto",
          hasLegend && "desktop:col-span-4"
        )}
      >
        <ResolvedHighchartsReact highcharts={Highcharts} options={optionsWithSeries} />
      </div>
      {hasLegend && (
        <div className="flex desktop:flex-col flex-wrap justify-center gap-xs overflow-hidden px-xs">
          {seriesFiltered.map((serie, index) => (
            <div key={serie.name} className="flex flex-row items-center gap-xs">
              <div
                key={serie.name}
                className="mr-xs flex desktop:min-w-0 desktop:max-w-full items-center gap-xs"
              >
                <div
                  className="h-sm w-sm shrink-0"
                  style={{
                    backgroundColor: serie.color ?? options.colors?.[index]?.toString() ?? ""
                  }}
                />
                <span className="min-w-0 truncate text-xs">{serie.name}</span>
                {serie.qoeWarning && (
                  <Tooltip body="QoE issue" placement="top">
                    <Icon
                      className="inline-flex text-orange-800"
                      icon={IconName.WARNING}
                      size={IconSize.MEDIUM}
                      type={IconVariant.SOLID}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : (
    <div className="mt-sm flex flex-col items-center gap-md">
      <img alt="Empty state illustration" className="h-[140px]" src={emptyEventList} />
      <div className="typo-sm-regular text-grey-600">No data yet.</div>
    </div>
  )
}

export default Chart
